import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, Truck, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { restaurantAdminService, rowsOf } from "../../../services/restaurantAdmin.service";

const SEEN_PREFIX = "food_manual_assignment_popup_seen:";
const POLL_MS = 10000;

function clean(value) {
  return String(value || "").trim();
}

function upper(value) {
  return clean(value).toUpperCase();
}

function getOrderId(order = {}) {
  return clean(order.id || order.orderId || order.order_id);
}

function getOrderNumber(order = {}) {
  return clean(order.orderNumber || order.order_number || order.orderNo || order.order_no || getOrderId(order));
}

function getServiceability(order = {}) {
  return (
    order.serviceabilitySnapshot ||
    order.serviceability_snapshot ||
    order.pricing?.serviceabilitySnapshot ||
    order.pricing?.serviceability_snapshot ||
    order.pricing?.dispatch?.serviceabilitySnapshot ||
    {}
  );
}

function isManualAssignmentRequired(order = {}) {
  const paymentStatus = upper(order.paymentStatus || order.payment_status);
  if (paymentStatus !== "PAID") return false;

  const status = upper(order.status);
  const driverStatus = upper(order.driverStatus || order.driver_status);
  const assignmentStatus = upper(order.driverAssignmentStatus || order.driver_assignment_status);
  const serviceability = getServiceability(order);
  const serviceAssignmentStatus = upper(serviceability.deliveryAssignmentStatus || serviceability.delivery_assignment_status);
  const serviceMode = upper(serviceability.deliveryAssignmentMode || serviceability.delivery_assignment_mode);

  return [status, driverStatus, assignmentStatus, serviceAssignmentStatus, serviceMode].some((value) =>
    ["MANUAL_ASSIGNMENT_REQUIRED", "MANUAL_REQUIRED", "PENDING_ASSIGNMENT", "NO_RIDER_AVAILABLE"].includes(value)
  );
}

function alreadySeen(orderId) {
  if (!orderId || typeof window === "undefined") return true;
  return window.localStorage.getItem(`${SEEN_PREFIX}${orderId}`) === "1";
}

function markSeen(orderId) {
  if (!orderId || typeof window === "undefined") return;
  window.localStorage.setItem(`${SEEN_PREFIX}${orderId}`, "1");
}

export default function FoodManualAssignmentPopup() {
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [hidden, setHidden] = useState(false);

  const orderId = useMemo(() => getOrderId(order || {}), [order]);
  const orderNumber = useMemo(() => getOrderNumber(order || {}), [order]);

  const checkOrders = useCallback(async () => {
    if (hidden || order) return;
    try {
      const res = await restaurantAdminService.listFoodOrders({ limit: 25, offset: 0 });
      const rows = rowsOf(res);
      const target = rows.find((item) => {
        const id = getOrderId(item);
        return id && isManualAssignmentRequired(item) && !alreadySeen(id);
      });
      if (target) setOrder(target);
    } catch (_err) {
      // Popup is best-effort. The orders page/manual assignment still works without this poll.
    }
  }, [hidden, order]);

  useEffect(() => {
    checkOrders();
    const timer = window.setInterval(checkOrders, POLL_MS);
    return () => window.clearInterval(timer);
  }, [checkOrders]);

  if (!order) return null;

  const close = () => {
    markSeen(orderId);
    setHidden(true);
    setOrder(null);
  };

  const openOrders = () => {
    markSeen(orderId);
    setOrder(null);
    navigate("/admin/food-dashboard/orders");
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/35 px-4 py-6 backdrop-blur-sm">
      <div className="w-full max-w-[460px] overflow-hidden rounded-[24px] border border-[#f2c6cc] bg-white shadow-[0_24px_70px_rgba(15,23,42,0.24)]">
        <div className="flex items-start gap-3 border-b border-[#f2c6cc] bg-[#fff8f9] px-5 py-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#fff1f3] text-[#d86a78]">
            <AlertTriangle size={24} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[16px] font-bold text-slate-900">Driver Assignment Needed</div>
            <div className="mt-1 text-[13px] leading-5 text-slate-500">
              Payment is successful, but auto assignment failed. Assign a driver manually from Orders.
            </div>
          </div>
          <button
            type="button"
            onClick={close}
            className="rounded-full p-1 text-slate-400 transition hover:bg-white hover:text-slate-700"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-5 py-4">
          <div className="rounded-2xl border border-[#f2c6cc] bg-white p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#fff1f3] text-[#d86a78]">
                <Truck size={19} />
              </div>
              <div>
                <div className="text-[12px] font-semibold uppercase tracking-wide text-slate-400">Order</div>
                <div className="text-[15px] font-bold text-slate-900">{orderNumber || "Order"}</div>
              </div>
            </div>
            <div className="mt-3 text-[13px] leading-5 text-slate-500">
              Status: <span className="font-semibold text-slate-800">Manual assignment required</span>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-2 border-t border-[#f2c6cc] bg-[#fff8f9] px-5 py-4 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={close}
            className="rounded-[12px] border border-[#f2c6cc] bg-white px-4 py-2.5 text-[13px] font-semibold text-slate-600 hover:bg-slate-50"
          >
            Later
          </button>
          <button
            type="button"
            onClick={openOrders}
            className="rounded-[12px] bg-[#d86a78] px-4 py-2.5 text-[13px] font-semibold text-white shadow-sm hover:bg-[#c95b69]"
          >
            Open Orders
          </button>
        </div>
      </div>
    </div>
  );
}
